import { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/contexts/AuthContext'
import { investorAdminApi } from '@/lib/api'

interface CampaignLink {
  id: number
  meta_campaign_id: string
  meta_ad_account_id: string
  investment_amount: number
  label: string | null
  created_at: string
}

interface InvestorDetail {
  id: number
  email: string
  name: string
  profit_share_percent: number | null
  created_at: string
  campaigns: CampaignLink[]
}

interface InvestorListItem {
  id: number
  email: string
  name: string
  profit_share_percent: number | null
  campaign_count: number
  total_invested: number
  created_at: string
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
}

export default function InvestorManagement() {
  const { user } = useAuth()
  const [investors, setInvestors] = useState<InvestorListItem[]>([])
  const [selected, setSelected] = useState<InvestorDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [showAddCampaign, setShowAddCampaign] = useState(false)
  const [editingProfitShare, setEditingProfitShare] = useState(false)
  const [profitShareInput, setProfitShareInput] = useState('')

  // Create form
  const [createForm, setCreateForm] = useState({ email: '', password: '', name: '', profit_share_percent: '' })

  // Add campaign form
  const [campaignForm, setCampaignForm] = useState({
    meta_campaign_id: '',
    meta_ad_account_id: '',
    investment_amount: '',
    label: '',
  })

  if (user?.role !== 'admin') {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-secondaryLabel)' }}>
        Access denied. Admin only.
      </div>
    )
  }

  const fetchInvestors = async () => {
    setLoading(true)
    try {
      const resp = await investorAdminApi.list()
      setInvestors(resp.data)
    } catch {
      toast.error('Failed to load investors')
    } finally {
      setLoading(false)
    }
  }

  const fetchDetail = async (id: number) => {
    try {
      const resp = await investorAdminApi.get(id)
      setSelected(resp.data)
      setProfitShareInput(resp.data.profit_share_percent !== null ? String(resp.data.profit_share_percent) : '')
      setEditingProfitShare(false)
    } catch {
      toast.error('Failed to load investor')
    }
  }

  useEffect(() => {
    fetchInvestors()
  }, [])

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password || !createForm.name || createForm.profit_share_percent === '') {
      toast.error('All fields are required')
      return
    }
    const profitShare = Number(createForm.profit_share_percent)
    if (Number.isNaN(profitShare) || profitShare < 0 || profitShare > 100) {
      toast.error('Profit share must be between 0 and 100')
      return
    }
    try {
      await investorAdminApi.create({
        email: createForm.email,
        password: createForm.password,
        name: createForm.name,
        profit_share_percent: profitShare,
      })
      toast.success('Investor created')
      setShowCreate(false)
      setCreateForm({ email: '', password: '', name: '', profit_share_percent: '' })
      fetchInvestors()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to create investor')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this investor and all their campaign links?')) return
    try {
      await investorAdminApi.delete(id)
      toast.success('Investor deleted')
      if (selected?.id === id) setSelected(null)
      fetchInvestors()
    } catch {
      toast.error('Failed to delete investor')
    }
  }

  const handleAddCampaign = async () => {
    if (!selected) return
    if (!campaignForm.meta_campaign_id || !campaignForm.meta_ad_account_id || !campaignForm.investment_amount) {
      toast.error('Campaign ID, Ad Account ID, and Investment Amount are required')
      return
    }
    try {
      await investorAdminApi.addCampaign(selected.id, {
        meta_campaign_id: campaignForm.meta_campaign_id,
        meta_ad_account_id: campaignForm.meta_ad_account_id,
        investment_amount: Math.round(parseFloat(campaignForm.investment_amount) * 100),
        label: campaignForm.label || undefined,
      })
      toast.success('Campaign linked')
      setShowAddCampaign(false)
      setCampaignForm({ meta_campaign_id: '', meta_ad_account_id: '', investment_amount: '', label: '' })
      fetchDetail(selected.id)
      fetchInvestors()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to link campaign')
    }
  }

  const handleRemoveCampaign = async (linkId: number) => {
    if (!selected) return
    if (!confirm('Remove this campaign link?')) return
    try {
      await investorAdminApi.removeCampaign(selected.id, linkId)
      toast.success('Campaign unlinked')
      fetchDetail(selected.id)
      fetchInvestors()
    } catch {
      toast.error('Failed to remove campaign')
    }
  }

  const handleSaveProfitShare = async () => {
    if (!selected) return
    const profitShare = Number(profitShareInput)
    if (Number.isNaN(profitShare) || profitShare < 0 || profitShare > 100) {
      toast.error('Profit share must be between 0 and 100')
      return
    }
    try {
      await investorAdminApi.update(selected.id, { profit_share_percent: profitShare })
      toast.success('Profit share updated')
      await fetchDetail(selected.id)
      await fetchInvestors()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update profit share')
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid var(--color-border)',
    background: 'var(--color-systemBackground)',
    color: 'var(--color-label)',
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-card)',
    borderRadius: 16,
    border: '1px solid var(--color-border)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    padding: 24,
    marginBottom: 16,
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--color-label)', letterSpacing: -0.5, margin: 0 }}>
          Investor Management
        </h1>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            padding: '10px 20px',
            borderRadius: 10,
            border: 'none',
            background: 'var(--color-primary)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + New Investor
        </button>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--color-label)' }}>
            Create Investor
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              placeholder="Name"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              style={inputStyle}
            />
            <input
              placeholder="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              style={inputStyle}
            />
            <input
              placeholder="Password"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              style={inputStyle}
            />
            <input
              placeholder="Profit Share (%)"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={createForm.profit_share_percent}
              onChange={(e) => setCreateForm({ ...createForm, profit_share_percent: e.target.value })}
              style={inputStyle}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleCreate}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: 'var(--color-primary)', color: '#fff', fontSize: 14,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                Create
              </button>
              <button
                onClick={() => setShowCreate(false)}
                style={{
                  padding: '10px 20px', borderRadius: 10,
                  border: '1px solid var(--color-border)', background: 'var(--color-systemBackground)',
                  color: 'var(--color-label)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investor list */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        {loading && <p style={{ color: 'var(--color-secondaryLabel)', textAlign: 'center', padding: 40 }}>Loading...</p>}
        {!loading && investors.length === 0 && (
          <p style={{ color: 'var(--color-secondaryLabel)', textAlign: 'center', padding: 40 }}>
            No investors yet. Create one above.
          </p>
        )}
        {!loading && investors.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
              <thead>
                <tr style={{ background: 'var(--color-secondarySystemBackground)' }}>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>Profit Share</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>Campaigns</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>Total Invested</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {investors.map((inv) => (
                  <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600, color: 'var(--color-label)' }}>{inv.name}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--color-secondaryLabel)' }}>{inv.email}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--color-label)' }}>
                      {inv.profit_share_percent !== null ? `${inv.profit_share_percent.toFixed(2)}%` : 'Not set'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--color-label)' }}>{inv.campaign_count}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--color-label)' }}>{formatCurrency(inv.total_invested)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => fetchDetail(inv.id)}
                          style={{
                            padding: '6px 14px', borderRadius: 8, border: '1px solid var(--color-border)',
                            background: 'var(--color-systemBackground)', color: 'var(--color-label)',
                            fontSize: 13, fontWeight: 500, cursor: 'pointer',
                          }}
                        >
                          Manage
                        </button>
                        <button
                          onClick={() => handleDelete(inv.id)}
                          style={{
                            padding: '6px 14px', borderRadius: 8, border: '1px solid var(--color-systemRed)',
                            background: 'transparent', color: 'var(--color-systemRed)',
                            fontSize: 13, fontWeight: 500, cursor: 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ ...cardStyle, marginTop: 24, borderColor: 'var(--color-primary)', borderWidth: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--color-label)' }}>{selected.name}</h2>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>{selected.email}</p>
              {!editingProfitShare ? (
                <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--color-secondaryLabel)' }}>
                  Profit Share: {selected.profit_share_percent !== null ? `${selected.profit_share_percent.toFixed(2)}%` : 'Not set'}
                </p>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={profitShareInput}
                    onChange={(e) => setProfitShareInput(e.target.value)}
                    style={{ ...inputStyle, width: 140, padding: '6px 10px', fontSize: 13 }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--color-secondaryLabel)' }}>%</span>
                  <button
                    onClick={handleSaveProfitShare}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: 'none',
                      background: 'var(--color-primary)', color: '#fff',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingProfitShare(false)
                      setProfitShareInput(selected.profit_share_percent !== null ? String(selected.profit_share_percent) : '')
                    }}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)',
                      background: 'var(--color-systemBackground)', color: 'var(--color-label)',
                      fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!editingProfitShare && (
                <button
                  onClick={() => setEditingProfitShare(true)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: '1px solid var(--color-border)',
                    background: 'var(--color-systemBackground)', color: 'var(--color-label)',
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  Edit Profit Share
                </button>
              )}
              <button
                onClick={() => setSelected(null)}
                style={{
                  padding: '6px 14px', borderRadius: 8, border: '1px solid var(--color-border)',
                  background: 'var(--color-systemBackground)', color: 'var(--color-label)',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>

          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-label)', margin: '0 0 12px' }}>
            Linked Campaigns
          </h3>

          {selected.campaigns.length === 0 && (
            <p style={{ color: 'var(--color-secondaryLabel)', fontSize: 14 }}>No campaigns linked yet.</p>
          )}

          {selected.campaigns.map((c) => (
            <div
              key={c.id}
              style={{
                padding: '12px 16px',
                borderRadius: 12,
                border: '1px solid var(--color-border)',
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-label)' }}>
                  {c.label || c.meta_campaign_id}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-secondaryLabel)' }}>
                  Campaign: {c.meta_campaign_id} &middot; Account: {c.meta_ad_account_id}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-secondaryLabel)' }}>
                  Investment: {formatCurrency(c.investment_amount)}
                </div>
              </div>
              <button
                onClick={() => handleRemoveCampaign(c.id)}
                style={{
                  padding: '4px 12px', borderRadius: 6, border: '1px solid var(--color-systemRed)',
                  background: 'transparent', color: 'var(--color-systemRed)',
                  fontSize: 12, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Remove
              </button>
            </div>
          ))}

          {/* Add campaign form */}
          {!showAddCampaign ? (
            <button
              onClick={() => setShowAddCampaign(true)}
              style={{
                marginTop: 12, padding: '10px 20px', borderRadius: 10, border: 'none',
                background: 'var(--color-primary)', color: '#fff', fontSize: 14,
                fontWeight: 600, cursor: 'pointer', width: '100%',
              }}
            >
              + Link Campaign
            </button>
          ) : (
            <div style={{ marginTop: 12, padding: 16, borderRadius: 12, background: 'var(--color-secondarySystemBackground)' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: 'var(--color-label)' }}>
                Link a Campaign
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  placeholder="Meta Campaign ID"
                  value={campaignForm.meta_campaign_id}
                  onChange={(e) => setCampaignForm({ ...campaignForm, meta_campaign_id: e.target.value })}
                  style={inputStyle}
                />
                <input
                  placeholder="Meta Ad Account ID (e.g. act_123456)"
                  value={campaignForm.meta_ad_account_id}
                  onChange={(e) => setCampaignForm({ ...campaignForm, meta_ad_account_id: e.target.value })}
                  style={inputStyle}
                />
                <input
                  placeholder="Investment Amount ($)"
                  type="number"
                  step="0.01"
                  value={campaignForm.investment_amount}
                  onChange={(e) => setCampaignForm({ ...campaignForm, investment_amount: e.target.value })}
                  style={inputStyle}
                />
                <input
                  placeholder="Label (optional friendly name)"
                  value={campaignForm.label}
                  onChange={(e) => setCampaignForm({ ...campaignForm, label: e.target.value })}
                  style={inputStyle}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={handleAddCampaign}
                    style={{
                      padding: '10px 20px', borderRadius: 10, border: 'none',
                      background: 'var(--color-primary)', color: '#fff', fontSize: 14,
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Link
                  </button>
                  <button
                    onClick={() => setShowAddCampaign(false)}
                    style={{
                      padding: '10px 20px', borderRadius: 10,
                      border: '1px solid var(--color-border)', background: 'var(--color-systemBackground)',
                      color: 'var(--color-label)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
